import { useCallback, useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import SettlementModalShell from '../common/SettlementModalShell.js';
import ShipmentDetailModal from '../shipment/ShipmentDetailModal.js';
import Loader from '../common/Loader.js';
import Button from '../common/Button.js';
import cashbookService from '../../services/CashbookService.js';
import { formatCurrencyPaise } from '../../utils/formatCurrency.js';
import { formatIndianDate } from '../../utils/localDate.js';

const money = (value) => formatCurrencyPaise(value, '₹', { alwaysPaise: true });
const DEFAULT_PAGE = 400;

const rowKind = (row, cardTitle) => {
  const text = String(row.detail || '').toLowerCase();
  if (text.includes('reversal')) return 'Reversal';
  if (text.includes('reserve')) return 'Reserve';
  return cardTitle || 'Line';
};

const rowTitle = (row) => [
  row.detail,
  row.party,
  row.route,
  row.date ? formatIndianDate(row.date) : '',
  row.payment_mode,
  row.status,
].filter(Boolean).join(' · ');

const DayBookCard = ({ card, onOpenLr, showDetail }) => {
  const pageSize = card.page_size || DEFAULT_PAGE;
  const allRows = card.rows || [];
  const [visible, setVisible] = useState(pageSize);
  const scrollRef = useRef(null);

  useEffect(() => {
    setVisible(pageSize);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [card.key, allRows.length, pageSize]);

  const shown = allRows.slice(0, visible);
  const hasMore = visible < allRows.length;

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !hasMore) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) {
      setVisible((v) => Math.min(v + pageSize, allRows.length));
    }
  }, [allRows.length, hasMore, pageSize]);

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col min-h-[240px] max-h-[340px]">
      <header className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-gray-800" title={card.hint}>{card.title}</h4>
        <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
          {card.count || 0}
        </span>
      </header>
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto"
        onScroll={onScroll}
      >
        {shown.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No LRs</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500 sticky top-0">
              <tr>
                <th className="px-3 py-1.5 text-left font-semibold w-10">S.No</th>
                <th className="px-3 py-1.5 text-left font-semibold">LR No</th>
                {showDetail ? (
                  <th className="px-3 py-1.5 text-left font-semibold">Type</th>
                ) : null}
                <th className="px-3 py-1.5 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((row, index) => {
                const negative = Number(row.amount) < 0;
                return (
                  <tr key={`${row.lr_no}-${row.date}-${index}`} className="hover:bg-gray-50" title={rowTitle(row)}>
                    <td className="px-3 py-2 text-gray-400">{index + 1}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="font-medium text-sky-700 hover:underline text-left"
                        onClick={() => onOpenLr(row.lr_no)}
                      >
                        {row.lr_no}
                      </button>
                    </td>
                    {showDetail ? (
                      <td className="px-3 py-2 text-xs text-gray-600 max-w-[120px] truncate">
                        {rowKind(row, card.title)}
                      </td>
                    ) : null}
                    <td
                      className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${
                        negative ? 'text-red-600' : 'text-gray-800'
                      }`}
                    >
                      {money(row.amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <footer className="px-3 py-2 bg-sky-50 border-t border-sky-100 flex items-center justify-between text-sm">
        <span className="font-medium text-sky-900">Total Amount</span>
        <span className="font-bold text-sky-950">{money(card.total)}</span>
      </footer>
    </section>
  );
};

export default function CashbookDayBookModal({
  open,
  onClose,
  fromDate,
  toDate,
  locationHubId,
  ownerUserId,
  ownerUserIds,
  branchLabel,
}) {
  const [lrNo, setLrNo] = useState('');
  const [appliedLr, setAppliedLr] = useState('');
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shipmentId, setShipmentId] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await cashbookService.getDayBook(
          fromDate,
          toDate,
          locationHubId,
          ownerUserId,
          appliedLr,
          ownerUserIds,
        );
        if (!cancelled) setBook(res?.data?.data || null);
      } catch (err) {
        if (!cancelled) {
          setBook(null);
          setError(err.response?.data?.error || 'Failed to load day book');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, fromDate, toDate, locationHubId, ownerUserId, ownerUserIds, appliedLr]);

  if (!open) return null;

  const cards = book?.cards || [];
  const rangeLabel = book?.from_date && book?.to_date
    ? `${formatIndianDate(book.from_date)} – ${formatIndianDate(book.to_date)}`
    : '';
  const branchPart = branchLabel ? ` · ${branchLabel}` : '';
  const subtitle = appliedLr
    ? `LR ${appliedLr}${branchPart}${rangeLabel ? ` · ${rangeLabel}` : ''}`
    : `${rangeLabel || 'Same dates as this cashbook'}${branchPart}`;
  const lrEmpty = appliedLr && cards.length > 0
    && cards.every((card) => !(card.rows || []).length);

  return (
    <>
      <SettlementModalShell
        title="Branch Day Book"
        subtitle={subtitle}
        onClose={onClose}
        size="xl"
      >
        <form
          className="flex justify-end gap-2 mb-3"
          onSubmit={(event) => {
            event.preventDefault();
            setAppliedLr(lrNo.trim());
          }}
        >
          <input
            value={lrNo}
            onChange={(event) => setLrNo(event.target.value)}
            placeholder="LR No"
            className="w-40 sm:w-52 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            aria-label="LR No"
          />
          <Button type="submit" variant="primary" icon={Search} size="sm">
            Search
          </Button>
          {appliedLr ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setLrNo('');
                setAppliedLr('');
              }}
            >
              Clear
            </Button>
          ) : null}
        </form>

        {loading ? (
          <Loader fullScreen={false} message="Loading day book..." />
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : (
          <>
            {lrEmpty ? (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-3">
                No lines for this LR at the selected branch and dates. Loading/unloading is
                posted to the branch that paid the labour (often booking or delivery hub), not
                every hub on the route.
              </p>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {cards.map((card) => (
                <DayBookCard
                  key={card.key}
                  card={card}
                  onOpenLr={setShipmentId}
                  showDetail={Boolean(appliedLr)}
                />
              ))}
            </div>
          </>
        )}
      </SettlementModalShell>
      <ShipmentDetailModal shipmentId={shipmentId} onClose={() => setShipmentId('')} />
    </>
  );
}
